/**
 * WordPress dependencies
 */
import { useSelect } from '@safe-wordpress/data';
import { createInterpolateElement } from '@safe-wordpress/element';
import { sprintf, _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { store as NAB_DATA } from '@nab/data';
import { ConversionActionScopeView } from '@nab/components';
import type { CAViewProps, ConversionActionScope, ProductId } from '@nab/types';

/**
 * Internal dependencies
 */
import type { Attributes } from './types';

export const View = ( props: CAViewProps< Attributes > ): JSX.Element => (
	<>
		<div>
			<ActualView { ...props } />
		</div>
		<ConversionActionScopeView scope={ props.scope } />
	</>
);

const ActualView = ( {
	attributes: { anyProduct, productId },
	scope,
}: CAViewProps< Attributes > ): JSX.Element => {
	const productTitle = useProductTitle( productId );

	if ( anyProduct ) {
		return <>{ getAnyProductLabel( scope ) }</>;
	}

	if ( ! productTitle ) {
		return <>{ getUnknownProductLabel( scope ) }</>;
	}

	return (
		<>
			{ createInterpolateElement(
				sprintf(
					getProductLabel( scope ),
					sprintf( '<strong>%s</strong>', productTitle )
				),
				{ strong: <strong /> }
			) }
		</>
	);
};

// =====
// HOOKS
// =====

const useProductTitle = ( productId: ProductId ) =>
	useSelect(
		( select ) =>
			select( NAB_DATA ).getEntityRecord( 'product', productId )?.title,
		[ productId ]
	);

// =======
// HELPERS
// =======

function getAnyProductLabel( scope: ConversionActionScope ): string {
	switch ( scope.type ) {
		case 'all-pages':
			return _x(
				'A visitor adds any product to the shopping cart on any page.',
				'text',
				'nelio-ab-testing'
			);

		case 'test-scope':
			return _x(
				'A visitor adds any product to the shopping cart on a tested page.',
				'text',
				'nelio-ab-testing'
			);

		case 'urls':
		case 'post-ids':
		case 'php-function':
			return _x(
				'A visitor adds any product to the shopping cart on certain pages.',
				'text',
				'nelio-ab-testing'
			);
	}
}

function getUnknownProductLabel( scope: ConversionActionScope ): string {
	switch ( scope.type ) {
		case 'all-pages':
			return _x(
				'A visitor adds a specific product to the shopping cart on any page.',
				'text',
				'nelio-ab-testing'
			);

		case 'test-scope':
			return _x(
				'A visitor adds a specific product to the shopping cart on a tested page.',
				'text',
				'nelio-ab-testing'
			);

		case 'urls':
		case 'post-ids':
		case 'php-function':
			return _x(
				'A visitor adds a specific product to the shopping cart on certain pages.',
				'text',
				'nelio-ab-testing'
			);
	}
}

function getProductLabel( scope: ConversionActionScope ): string {
	switch ( scope.type ) {
		case 'all-pages':
			/* translators: %s: The name of a product. */
			return _x(
				'A visitor adds the product %s to the shopping cart on any page.',
				'text',
				'nelio-ab-testing'
			);

		case 'test-scope':
			/* translators: %s: The name of a product. */
			return _x(
				'A visitor adds the product %s to the shopping cart on a tested page.',
				'text',
				'nelio-ab-testing'
			);

		case 'urls':
		case 'post-ids':
		case 'php-function':
			/* translators: %s: The name of a product. */
			return _x(
				'A visitor adds the product %s to the shopping cart on certain pages.',
				'text',
				'nelio-ab-testing'
			);
	}
}
